import EditIcon from '@mui/icons-material/Edit'
import HistoryIcon from '@mui/icons-material/History'
import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  Link,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { yellow } from '@mui/material/colors'
import { useCallback, useEffect, useMemo, useState } from 'react'
import type { SyntheticEvent } from 'react'
import { useDispatch } from 'react-redux'
import { queueNotification } from '~/shared/notifications/actions'
import {
  type BffCrmScheduleDraftsPendingRetrieveResponse,
  physician,
  type PhysicianListResponse,
  type ScheduleVisitMixRatiosCreateBodyParams,
} from '@fireflyhealth/api'
import { required } from '~/shared/lib/required'
import { logger } from '~/shared/lib/logger'

type Physician = PhysicianListResponse[number]
import {
  getAppointmentTypes,
  getSchedulesForProvider,
  useCreateAppointmentTypeMapping,
  useCreateOrUpdateVisitMix,
  useCreateScheduleIngestionJob,
  useGetHistoricalShiftExceptions,
  useGetHistoricalShifts,
  useGetPendingScheduleDrafts,
  useGetScheduleIngestionJob,
  useDeleteAppointmentTypeMapping,
  useUpdateIngestionJobStatus,
} from './ScheduleService'
import { useMutation } from '~/shared/hooks/useMutation'
import { Loader } from '~/shared/ui'
import EditShiftExceptionTimingsModal from './ProviderShift/EditShiftExceptionTimingsModal'
import EditShiftTimingsModal from './ProviderShift/EditShiftTimingsModal'
import EditVisitMixModal from './ProviderShift/EditVisitMixModal'
import {
  APPROVED_STATUS,
  AppointmentType,
  EXCEPTIONSTAB,
  IngestionImpact,
  Provider,
  REJECTED_STATUS,
  REVIEW_PENDING_STATUS,
  Schedule,
  Shift,
  ShiftException,
  TABS,
  TAB_KEYS,
  VisitMix,
  formatEffectivePeriod,
  formatPeriodDate,
  formatPeriodTime,
  formatTime,
  getIsAllDay,
  getShiftExceptions,
  getShifts,
  groupAppointmentTypesByVisitMixGroup,
  sortShiftExceptionFunction,
  sortShiftFunction,
  isShiftStartTimeInPast,
  isShiftExceptionStartDateInPast,
  FIRST_TAB,
  type ShiftTab,
} from './ProviderShift/utils'
import AppointmentTypeMappingSummary from './ProviderShift/AppointmentTypeMappingSummary'
import { fireAndForget } from '~/shared/lib/fireAndForget'
import { haveChanged } from '~/shared/lib/haveChanged'

const TABLE_SX = { '& .MuiTableCell-root': { padding: '4px' } }

// `!important` beats TABLE_SX's cell padding, which is more specific.
const CELL_SX = { pr: '1em !important' }

const DATE_CELL_SX = { ...CELL_SX, maxWidth: '120px' }

const ProviderShiftSettings = () => {
  const dispatch = useDispatch()
  const [providerFilter, setProviderFilter] = useState<Record<string, Physician>>({})
  const [providerSchedules, setProviderSchedules] = useState<Record<string, Schedule>>({})
  const [pendingDrafts, setPendingDrafts] = useState<
    BffCrmScheduleDraftsPendingRetrieveResponse['drafts']
  >([])
  const [hasPendingDrafts, setHasPendingDrafts] = useState(false)
  const [selectedProvider, setSelectedProvider] = useState('')
  const [selectedTab, setSelectedTab] = useState<ShiftTab>(FIRST_TAB)
  const selectedDay = selectedTab === EXCEPTIONSTAB ? undefined : TABS[selectedTab]
  const [shiftsData, setShiftsData] = useState<Shift[]>([])
  const [shiftExceptionsData, setShiftExceptionsData] = useState<ShiftException[]>([])
  const [visitMixData, setVisitMixData] = useState<Record<number, Record<string, VisitMix>>>({})
  const [appointmentTypesData, setAppointmentTypesData] = useState<AppointmentType[]>([])
  const [ingestionJobId, setIngestionJobId] = useState<number | null>(null)
  const [currentShiftsData, setCurrentShiftsData] = useState<Shift[]>([])
  const [isHistory, setIsHistory] = useState<boolean>(false)
  const [editingShiftsData, setEditingShiftsData] = useState<Shift[] | null>(null)
  const [editingShiftExceptionsData, setEditingShiftExceptionsData] = useState<
    ShiftException[] | null
  >(null)
  const [editingVisitMixData, setEditingVisitMixData] = useState<Record<
    number,
    Record<string, VisitMix>
  > | null>(null)
  const [isVisitMixEdited, setIsVisitMixEdited] = useState<boolean>(false)
  const [isIngestionInProgress, setIsIngestionInProgress] = useState<boolean>(false)
  const [hasReviewPendingJob, setHasReviewPendingJob] = useState<boolean>(false)
  const [loadingProvider, setLoadingProvider] = useState<string | null>(null)
  const [providerData, setProviderData] = useState<Provider>()

  const [impactData, setImpactData] = useState<IngestionImpact>()

  // Pending changes state (collect all changes before submitting)
  const [pendingShiftChanges, setPendingShiftChanges] = useState<{
    [dayOfWeek: number]: { edited: Shift[]; deleted: Shift[] }
  }>({})
  const [pendingExceptionChanges, setPendingExceptionChanges] = useState<{
    edited: ShiftException[]
    deleted: ShiftException[]
  }>({ edited: [], deleted: [] })
  const [hasPendingChanges, setHasPendingChanges] = useState<boolean>(false)

  // Compute grouped appointment types (including ungrouped) from appointment types
  const groupedAppointmentTypes = useMemo(
    () => groupAppointmentTypesByVisitMixGroup(appointmentTypesData),
    [appointmentTypesData],
  )

  const { mutateAsync: handleUpdateIngestionJobStatus } = useUpdateIngestionJobStatus()

  const {
    isPending: isCreatingScheduleIngestionJob,
    mutateAsync: handleCreateScheduleIngestionJob,
  } = useCreateScheduleIngestionJob()
  const {
    isPending: isGettingScheduleIngestionJobImpact,
    mutateAsync: handleGetScheduleIngestionJobImpact,
  } = useGetScheduleIngestionJob()
  const { isPending: _isLoadingPendingDrafts, mutateAsync: handleGetPendingDrafts } =
    useGetPendingScheduleDrafts()

  const { mutateAsync: handleCreateOrUpdateVisitMixRatio } = useCreateOrUpdateVisitMix()

  const { mutateAsync: handleCreateAppointmentTypeMapping } = useCreateAppointmentTypeMapping()
  const { mutateAsync: handleDeleteAppointmentTypeMapping } = useDeleteAppointmentTypeMapping()

  const { isPending: isLoadingHistoricalShifts, mutateAsync: handleGetHistoricalShifts } =
    useGetHistoricalShifts()
  const {
    isPending: isLoadingHistoricalShiftExceptions,
    mutateAsync: handleGetHistoricalShiftExceptions,
  } = useGetHistoricalShiftExceptions()

  const handleTabChange = (_: SyntheticEvent, newValue: ShiftTab) => {
    setSelectedTab(newValue)
    if (newValue !== EXCEPTIONSTAB)
      handleShiftsData(shiftsData.filter(shift => shift.day_of_week == TABS[newValue].dayOfWeek))
  }

  const fetchSchedules = useCallback(async () => {
    const { data: physicians } = await physician.list()
    const apptTypes = await getAppointmentTypes()

    setAppointmentTypesData(apptTypes)
    const providerFilterData: Record<string, Physician> = {}
    physicians.forEach(physician => {
      const providerName = physician.first_name + ' ' + physician.last_name
      providerFilterData[providerName] = physician
    })
    setProviderFilter(providerFilterData)
  }, [])

  const { loading: loadingSchedules, handler: loadSchedules } = useMutation(fetchSchedules)

  const { loading: loadingScheduleForProvider, handler: loadSchedulesForProvider } = useMutation(
    async (provider: string) => {
      const physician = providerFilter[provider]
      if (!physician) return null
      const [scheduleData] = await getSchedulesForProvider(physician.provider.pk)
      if (!scheduleData) return null
      setProviderSchedules(prevSchedules => {
        return { ...prevSchedules, [provider]: scheduleData }
      })
      return scheduleData
    },
  )

  const fetchPendingDrafts = useCallback(async () => {
    const result = await handleGetPendingDrafts()
    setPendingDrafts(result.drafts)
    setHasPendingDrafts(result.drafts.length > 0)
    return result
  }, [handleGetPendingDrafts])

  const { loading: loadingPendingDrafts, run: loadPendingDrafts } = useMutation(fetchPendingDrafts)

  const handleExceptionData = (exceptionData: ShiftException[]) => {
    const currentData: ShiftException[] = []

    exceptionData.forEach(item => {
      const currentDate = new Date().toISOString().slice(0, 10)
      const upperDate = item.period.upper

      if (!upperDate || upperDate >= currentDate) {
        currentData.push(item)
      }
    })

    setShiftExceptionsData(currentData)
  }

  const handleShiftsData = (perDayShiftData: Shift[]) => {
    const currentData: Shift[] = []

    perDayShiftData.forEach(item => {
      const currentDate = new Date().toISOString().slice(0, 10)
      const upperDate = item.effective_period.upper

      if (!upperDate || upperDate >= currentDate) {
        currentData.push(item)
      }
    })

    setCurrentShiftsData(currentData)
  }

  const handleEditProvider = async (provider: string) => {
    try {
      setLoadingProvider(provider)
      const physicianData = providerFilter[provider]

      // Always load fresh schedule data to avoid stale cache after ingestion jobs complete
      const loadedScheduleData = await loadSchedulesForProvider(provider)
      const scheduleData = loadedScheduleData ?? providerSchedules[provider]
      if (!scheduleData) {
        logger.error('[Shifts] no schedule data for provider', provider)
        return
      }
      setProviderData({
        ...scheduleData.provider,
      })

      let allShifts: Shift[]
      let allShiftExceptions: ShiftException[]
      const [latestJob] = scheduleData.ingestion_jobs
      const latestJobId = latestJob?.id
      if (latestJob && latestJobId) {
        if (latestJob.review_status == APPROVED_STATUS) {
          const isProviderInPendingDrafts = pendingDrafts.some(
            draft => draft.provider_id === physicianData?.provider.pk,
          )
          setIsIngestionInProgress(isProviderInPendingDrafts)
          setIngestionJobId(null)
          setHasReviewPendingJob(false)
        } else {
          setIngestionJobId(latestJobId)
          setIsIngestionInProgress(false)
          setHasReviewPendingJob(latestJob.review_status === REVIEW_PENDING_STATUS)
          fireAndForget('[Shifts] update ingestion job impact', () =>
            updateIngestionJobImpact(latestJobId),
          )
        }
        // Clear any local pending changes to avoid attempting to create another draft
        setPendingShiftChanges({})
        setPendingExceptionChanges({ edited: [], deleted: [] })
        setHasPendingChanges(false)

        allShifts = getShifts(latestJob.staging_shifts).sort(sortShiftFunction)
        allShiftExceptions = getShiftExceptions(latestJob.staging_shift_exceptions).sort(
          sortShiftExceptionFunction,
        )
      } else {
        setIngestionJobId(null)
        setIsIngestionInProgress(false)
        setHasReviewPendingJob(false)
        allShifts = scheduleData.shifts
          .map(shift => {
            return { ...shift, isEdited: false }
          })
          .sort(sortShiftFunction)
        allShiftExceptions = scheduleData.shift_exceptions
          .map(shiftException => {
            return {
              ...shiftException,
              isEdited: false,
              isAllDay: getIsAllDay(shiftException.period),
            }
          })
          .sort(sortShiftExceptionFunction)
      }
      setShiftsData(allShifts)
      handleExceptionData(allShiftExceptions)
      setSelectedTab(FIRST_TAB)
      handleShiftsData(allShifts.filter(shift => shift.day_of_week === TABS[FIRST_TAB].dayOfWeek))
      // Initialize visit mix ratios by group
      // We use group-based keys (group_<id>) and store the percentage at the group level
      // When saving, we'll distribute the group percentage to all appointment types in the group
      const visitMixRatios: Record<number, Record<string, VisitMix>> = {}
      Object.values(TABS).forEach(tab => (visitMixRatios[tab.dayOfWeek] = {}))

      // Group the existing ratios by visit mix group
      // Take the first ratio found for each group (they should all be the same)
      scheduleData.appointment_type_mappings.forEach(mapping => {
        const groupId = mapping.appointment_type.visit_mix_group?.id
        if (!groupId) return

        const groupKey = `group_${groupId}`
        mapping.visit_mix_ratios.forEach(visitMix => {
          // Only set if not already set (take the first one for the group)
          const dayRatios = (visitMixRatios[visitMix.day_of_week] ??= {})
          dayRatios[groupKey] ??= {
            id: visitMix.id,
            percentage: visitMix.percentage_of_slots,
            isEdited: false,
          }
        })
      })
      setVisitMixData(visitMixRatios)
    } catch (error) {
      logger.error('[Shifts] load provider data', error)
    } finally {
      setLoadingProvider(null)
    }
  }

  const onProviderChange = (selected: string) => {
    if (selected && providerFilter[selected]) {
      setSelectedProvider(selected)
    } else {
      setSelectedProvider('')
    }
  }

  // One flow from the visit-mix upserts through the status update to the reloads, so the buttons
  // stay disabled until it settles. Each request inside reports its own failure.
  const { run: updateIngestionJob, loading: isUpdatingIngestionJob } = useMutation(
    async (reviewStatus: string) => {
      if (
        isVisitMixEdited &&
        providerData &&
        appointmentTypesData &&
        reviewStatus == APPROVED_STATUS
      ) {
        // Visit-mix ratios are keyed by Physician (PhysicianVisitMixRatio), so they can't exist without one.
        // The Visit Mix editor is disabled for these providers (see render), so this guard is a
        // defensive no-op that also narrows physicianId to a number for the push below.
        const physicianId = providerData.physician
        if (physicianId != null) {
          const editedVisitData: ({
            id: number | null
          } & ScheduleVisitMixRatiosCreateBodyParams)[] = []
          const scheduleForVisitMix =
            selectedProvider && providerSchedules[selectedProvider]
              ? providerSchedules[selectedProvider]
              : undefined
          const enrolledAppointmentTypeKeys = new Set(
            scheduleForVisitMix?.appointment_type_mappings?.map(
              mapping => mapping.appointment_type.unique_key,
            ) ?? [],
          )

          // Build a map of groupId -> appointment types in that group
          const groupToAppointmentTypes = new Map<number, AppointmentType[]>()
          appointmentTypesData.forEach(apptType => {
            if (!apptType.visit_mix_group) return
            const groupId = apptType.visit_mix_group.id
            const groupTypes = groupToAppointmentTypes.get(groupId) ?? []
            groupTypes.push(apptType)
            groupToAppointmentTypes.set(groupId, groupTypes)
          })

          // For each edited group, distribute the percentage to all enrolled appointment types in that group
          Object.entries(visitMixData).forEach(([dayOfWeek, dayMix]) => {
            Object.entries(dayMix).forEach(([groupKey, groupMix]) => {
              // Only process group keys (format: group_<id>)
              if (!groupKey.startsWith('group_')) return

              const groupId = parseInt(groupKey.replace('group_', ''))

              if (!groupMix.isEdited) return

              // Get all appointment types in this group
              const groupApptTypes = groupToAppointmentTypes.get(groupId) ?? []

              // Create visit mix entries for all enrolled appointment types in the group
              groupApptTypes.forEach(apptType => {
                if (enrolledAppointmentTypeKeys.has(apptType.unique_key)) {
                  editedVisitData.push({
                    physician_id: physicianId,
                    appointment_type_id: apptType.id,
                    day_of_week: dayOfWeek as ScheduleVisitMixRatiosCreateBodyParams['day_of_week'],
                    percentage_of_slots: groupMix.percentage,
                    id: null, // Always create new, backend will handle upsert
                  })
                }
              })
            })
          })
          await Promise.all(
            editedVisitData.map(visitMix => handleCreateOrUpdateVisitMixRatio(visitMix)),
          )
        }
      }
      if (ingestionJobId) {
        await handleUpdateIngestionJobStatus({
          jobId: ingestionJobId,
          review_status: reviewStatus as 'Approved' | 'Rejected',
        })
      }
      void loadSchedulesForProvider(selectedProvider)
      loadPendingDrafts()
      setProviderData(undefined)

      setImpactData(undefined)
      setHasReviewPendingJob(false)
      setIsVisitMixEdited(false)
    },
  )

  const updateIngestionJobImpact = async (jobId: number) => {
    const ingestionImpact = await handleGetScheduleIngestionJobImpact(jobId)
    setImpactData(ingestionImpact)
  }

  // Common utility functions for BFF API transformation
  const formatTimeToHMS = (time: string) => {
    if (!time) return time
    return time.includes(':') && time.split(':').length === 2 ? `${time}:00` : time
  }

  // Writes a dynamic key, so the payload is indexed through one localized assertion rather
  // than being typed as `any` all the way out to the caller.
  const addIdIfValid = <R extends object>(
    payload: R,
    id: number | undefined | null,
    idFieldName: string,
  ) => {
    if (typeof id === 'number' && id > 0) {
      ;(payload as Record<string, unknown>)[idFieldName] = id
    }
  }

  // Generic transformation function for BFF API records
  const transformRecordForBffApi = <T extends { id?: number }, R extends object>(
    record: T,
    idFieldName: string,
    dataTransformer: (record: T) => R,
  ) => {
    const payload = dataTransformer(record)
    addIdIfValid(payload, record.id, idFieldName)
    return payload
  }

  const transformShiftForBffApi = (shift: Shift) => {
    return transformRecordForBffApi(shift, 'shift_id', shift => ({
      day_of_week: shift.day_of_week,
      start_time: formatTimeToHMS(shift.start_time),
      stop_time: formatTimeToHMS(shift.stop_time),
      effective_period: {
        lower: shift.effective_period?.lower || null,
        upper: shift.effective_period?.upper || null,
      },
    }))
  }

  const transformShiftExceptionForBffApi = (exception: ShiftException) => {
    return transformRecordForBffApi(exception, 'shift_exception_id', exception => ({
      period: {
        lower: exception.period?.lower || null,
        upper: exception.period?.upper || null,
      },
      reason: exception.reason || null,
    }))
  }

  // Handle shift submission with pending changes
  const handleShiftSubmitWithPendingChanges = (
    editedShiftsData: Shift[],
    deletedShiftsData: Shift[],
  ) => {
    const currentDayOfWeek = required(selectedDay, 'a day tab').dayOfWeek

    // Update pending changes for this day
    setPendingShiftChanges(prev => ({
      ...prev,
      [currentDayOfWeek]: {
        edited: editedShiftsData,
        deleted: deletedShiftsData,
      },
    }))

    // Update local shifts data to reflect changes in UI
    // Remove existing shifts for this day and add the new ones
    const allShiftsData: Shift[] = [
      ...shiftsData.filter(shift => shift.day_of_week !== currentDayOfWeek),
      ...editedShiftsData,
    ]
    setShiftsData(allShiftsData)

    // Update current shifts display for this day
    handleShiftsData(
      editedShiftsData.map(shift => {
        return { ...shift, isEdited: false }
      }),
    )

    // Mark that we have pending changes
    setHasPendingChanges(true)
  }

  const handleShiftSubmit = (editedShiftsData: Shift[], deletedShiftsData: Shift[]) => {
    // Collect changes locally, don't submit immediately
    handleShiftSubmitWithPendingChanges(editedShiftsData, deletedShiftsData)
    setEditingShiftsData(null)
  }

  const handleVisitMixSubmit = (visitMix: Record<number, Record<string, VisitMix>>) => {
    setVisitMixData(visitMix)
    setIsVisitMixEdited(true)
    setEditingVisitMixData(null)
  }

  const loadHistoricalShifts = async (dayOfWeek: number): Promise<Shift[]> => {
    if (!providerData) return []

    try {
      const response = await handleGetHistoricalShifts({
        providerId: providerData.pk,
        dayOfWeek: dayOfWeek,
      })
      return response.historical_shifts
    } catch (error) {
      logger.error('[Shifts] load historical shifts', error)
      throw error
    }
  }

  const loadHistoricalShiftExceptions = async (): Promise<ShiftException[]> => {
    if (!providerData) return []

    try {
      const response = await handleGetHistoricalShiftExceptions({
        providerId: providerData.pk,
      })
      // Add isAllDay property to historical shift exceptions for proper checkbox functionality
      return response.historical_shift_exceptions.map(shiftException => ({
        ...shiftException,
        isAllDay: getIsAllDay(shiftException.period),
      }))
    } catch (error) {
      logger.error('[Shifts] load historical shift exceptions', error)
      throw error
    }
  }

  const handleHistoryButtonClick = async () => {
    try {
      setIsHistory(true)
      const historicalShifts = await loadHistoricalShifts(
        required(selectedDay, 'a day tab').dayOfWeek,
      )
      // Always set the data, even if it's an empty array, so the modal shows
      setEditingShiftsData(historicalShifts || [])
    } catch (error) {
      logger.error('[Shifts] open shift history', error)
      setIsHistory(false) // Reset the history state if there's an error
    }
  }

  const handleHistoryExceptionButtonClick = async () => {
    try {
      setIsHistory(true)
      const historicalShiftExceptions = await loadHistoricalShiftExceptions()
      // Always set the data, even if it's an empty array, so the modal shows
      setEditingShiftExceptionsData(historicalShiftExceptions || [])
    } catch (error) {
      logger.error('[Shifts] open shift exception history', error)
      setIsHistory(false) // Reset the history state if there's an error
    }
  }

  // Handle shift exception submission with pending changes
  const handleShiftExceptionSubmitWithPendingChanges = (
    editedShiftExceptionsData: ShiftException[],
    deletedShiftExceptionsData: ShiftException[],
  ) => {
    // Update pending exception changes
    setPendingExceptionChanges({
      edited: editedShiftExceptionsData,
      deleted: deletedShiftExceptionsData,
    })

    // Update local exception data to reflect changes in UI
    setShiftExceptionsData(editedShiftExceptionsData)
    handleExceptionData(
      editedShiftExceptionsData.map(shiftException => {
        return { ...shiftException, isEdited: false }
      }),
    )

    // Mark that we have pending changes
    setHasPendingChanges(true)
  }

  const handleShiftExceptionSubmit = (
    editedShiftExceptionsData: ShiftException[],
    deletedShiftExceptionsData: ShiftException[],
  ) => {
    // Collect changes locally, don't submit immediately
    handleShiftExceptionSubmitWithPendingChanges(
      editedShiftExceptionsData,
      deletedShiftExceptionsData,
    )
    setEditingShiftExceptionsData(null)
  }

  // Submit all pending changes at once
  const handleSubmitAllPendingChanges = async () => {
    if (!providerData || !hasPendingChanges) return

    try {
      // Collect all shifts from pending changes
      const allShiftsData: Shift[] = []

      // Add shifts from all days, prioritizing pending changes
      Object.values(TABS).forEach(tab => {
        const dayOfWeek = tab.dayOfWeek
        if (pendingShiftChanges[dayOfWeek]) {
          // Use pending changes for this day
          allShiftsData.push(...pendingShiftChanges[dayOfWeek].edited)
        } else {
          // Use existing shifts for this day
          allShiftsData.push(...shiftsData.filter(shift => shift.day_of_week === dayOfWeek))
        }
      })

      // Use pending exceptions or existing ones
      const allExceptionsData =
        pendingExceptionChanges.edited.length > 0
          ? pendingExceptionChanges.edited
          : shiftExceptionsData

      // Validate new shifts for past time (only for new shifts with id < 0)
      const invalidShifts = allShiftsData.filter(shift => {
        return (
          shift.id < 0 &&
          shift.effective_period.lower &&
          shift.start_time &&
          isShiftStartTimeInPast(shift.effective_period.lower, shift.start_time)
        )
      })

      // Validate new shift exceptions for past time (only for new exceptions with id < 0)
      const invalidExceptions = allExceptionsData.filter(exception => {
        return (
          exception.id < 0 &&
          exception.period.lower &&
          isShiftExceptionStartDateInPast(exception.period.lower)
        )
      })

      // Validate deleted exceptions for past time (cannot delete past or earlier-today exceptions)
      const invalidDeletedExceptions = pendingExceptionChanges.deleted.filter(exception => {
        return exception.period.lower && isShiftExceptionStartDateInPast(exception.period.lower)
      })

      // If there are validation errors, show them and prevent submission
      if (
        invalidShifts.length > 0 ||
        invalidExceptions.length > 0 ||
        invalidDeletedExceptions.length > 0
      ) {
        const errorMessages: string[] = []

        if (invalidShifts.length > 0) {
          errorMessages.push(
            `Cannot schedule ${invalidShifts.length} shift${
              invalidShifts.length > 1 ? 's' : ''
            } in the past`,
          )
        }

        if (invalidExceptions.length > 0) {
          errorMessages.push(
            `Cannot schedule ${invalidExceptions.length} exception${
              invalidExceptions.length > 1 ? 's' : ''
            } in the past`,
          )
        }

        if (invalidDeletedExceptions.length > 0) {
          errorMessages.push(
            `Cannot delete ${invalidDeletedExceptions.length} exception${
              invalidDeletedExceptions.length > 1 ? 's' : ''
            } that start${
              invalidDeletedExceptions.length > 1 ? '' : 's'
            } in the past or earlier today`,
          )
        }

        // Set validation errors to display in the UI
        setImpactData(prev => ({
          ...prev,
          validation_errors: errorMessages,
          impacted_appointments: prev?.impacted_appointments ?? [],
          impacted_appointment_slots: prev?.impacted_appointment_slots ?? [],
        }))

        logger.error('[Shifts] validation errors', errorMessages)
        return
      }

      // Transform data for BFF API
      const transformedShifts = allShiftsData.map(transformShiftForBffApi)
      const transformedExceptions = allExceptionsData.map(transformShiftExceptionForBffApi)

      const payload = {
        provider_id: providerData.pk,
        shifts: transformedShifts,
        shift_exceptions: transformedExceptions,
      }

      const response = await handleCreateScheduleIngestionJob(payload)
      setIngestionJobId(response.id)

      // Clear pending changes
      setPendingShiftChanges({})
      setPendingExceptionChanges({ edited: [], deleted: [] })
      setHasPendingChanges(false)

      // Update impact data
      fireAndForget('[Shifts] update ingestion job impact', () =>
        updateIngestionJobImpact(response.id),
      )
    } catch (error) {
      logger.error('[Shifts] submit pending changes', error)
    }
  }

  // Clear all pending changes
  const handleClearPendingChanges = () => {
    setPendingShiftChanges({})
    setPendingExceptionChanges({ edited: [], deleted: [] })
    setHasPendingChanges(false)

    // Reset UI state to original data
    if (selectedProvider && providerSchedules[selectedProvider]) {
      const schedule = providerSchedules[selectedProvider]

      // Reset shifts data to original
      const allShifts = schedule.shifts.sort(sortShiftFunction)
      setShiftsData(allShifts)

      // Reset current shifts data for the selected tab
      if (selectedTab !== EXCEPTIONSTAB) {
        const dayShifts = allShifts.filter(
          shift => shift.day_of_week === TABS[selectedTab].dayOfWeek,
        )
        handleShiftsData(dayShifts)
      }

      // Reset exceptions data to original
      const allExceptions = schedule.shift_exceptions.sort(sortShiftExceptionFunction)
      setShiftExceptionsData(allExceptions)
      handleExceptionData(allExceptions)

      // Clear any ingestion job state
      setIngestionJobId(null)
      setImpactData(undefined)
      setHasReviewPendingJob(false)
    }
  }

  // A different provider clears the pending changes and returns to the first tab.
  const [providerShown, setProviderShown] = useState<typeof selectedProvider>()
  if (selectedProvider !== providerShown) {
    setProviderShown(selectedProvider)
    setPendingShiftChanges({})
    setPendingExceptionChanges({ edited: [], deleted: [] })
    setHasPendingChanges(false)
    setHasReviewPendingJob(false)
  }

  // Schedules and drafts reload whenever their loaders change, each time from the first tab.
  const loaders = [loadSchedules, loadPendingDrafts]
  const [loadedWith, setLoadedWith] = useState<unknown[]>()
  if (haveChanged(loadedWith, loaders)) {
    setLoadedWith(loaders)
    setSelectedTab(FIRST_TAB)
  }
  useEffect(() => {
    void loadSchedules()
    loadPendingDrafts()
  }, [loadSchedules, loadPendingDrafts])

  const currentSchedule = selectedProvider ? providerSchedules[selectedProvider] : undefined

  const mappedAppointmentTypeKeys = new Set(
    currentSchedule?.appointment_type_mappings?.map(
      mapping => mapping.appointment_type.unique_key,
    ) ?? [],
  )

  // Check if unmapping an appointment type would leave a group with non-zero visit mix
  const getUnmapValidationError = (
    appointmentType: AppointmentType,
  ): { groupId: number; groupTitle: string } | null => {
    const groupId = appointmentType.visit_mix_group?.id
    if (!groupId) return null // No group, no validation needed

    // Count how many mapped appointment types are in this group (excluding the one being unmapped)
    const mappedInGroup = currentSchedule?.appointment_type_mappings?.filter(mapping => {
      return (
        mapping.appointment_type.visit_mix_group?.id === groupId &&
        mapping.appointment_type.id !== appointmentType.id
      )
    })

    // If there are other mapped appointment types in this group, no validation needed
    if (mappedInGroup && mappedInGroup.length > 0) return null

    // This is the last mapped appointment type in the group
    // Check if visit mix ratio is non-zero for this group on ANY day
    const groupKey = `group_${groupId}`
    const daysWithNonZeroRatio: string[] = []

    Object.entries(TABS).forEach(([dayKey, { dayOfWeek }]) => {
      const dayMix = visitMixData[dayOfWeek]
      if (dayMix?.[groupKey] && dayMix[groupKey].percentage > 0) {
        daysWithNonZeroRatio.push(dayKey)
      }
    })

    if (daysWithNonZeroRatio.length > 0) {
      return {
        groupId,
        groupTitle: appointmentType.visit_mix_group?.title ?? 'Unknown',
      }
    }

    return null
  }

  const { run: toggleAppointmentTypeMapping, loading: isUpdatingAppointmentTypeMapping } =
    useMutation(
      async (appointmentType: AppointmentType, isProviderEnrolledInAppointmentType: boolean) => {
        if (!providerData || !selectedProvider || providerData.physician == null) return

        const payload = {
          physicianId: providerData.physician,
          appointmentTypeId: appointmentType.id,
        }

        const updateScheduleMappings = (
          prev: typeof providerSchedules,
          updater: (
            mappings: Schedule['appointment_type_mappings'],
          ) => Schedule['appointment_type_mappings'],
        ) => {
          const schedule = prev[selectedProvider]
          if (!schedule) return prev
          return {
            ...prev,
            [selectedProvider]: {
              ...schedule,
              appointment_type_mappings: updater(schedule.appointment_type_mappings || []),
            },
          }
        }

        if (isProviderEnrolledInAppointmentType) {
          await handleCreateAppointmentTypeMapping(payload)
          setProviderSchedules(prev =>
            updateScheduleMappings(prev, mappings => [
              ...mappings,
              { appointment_type: appointmentType, visit_mix_ratios: [] },
            ]),
          )
        } else {
          // Validate before unmapping: check if this would leave a group with non-zero visit mix
          const validationError = getUnmapValidationError(appointmentType)
          if (validationError) {
            dispatch(
              queueNotification({
                variant: 'warning',
                message: `Cannot unmap the last appointment type in "${validationError.groupTitle}" group. Please set the visit mix ratio to 0% for this group on all days first.`,
              }),
            )
            return // Prevent unmapping
          }

          await handleDeleteAppointmentTypeMapping(payload)
          setProviderSchedules(prev =>
            updateScheduleMappings(prev, mappings =>
              mappings.filter(m => m.appointment_type.id !== appointmentType.id),
            ),
          )
          // Clear any local visit mix ratios for this appointment type so UI reflects cascade delete
          setVisitMixData(prev => {
            const next: Record<number, Record<string, VisitMix>> = { ...prev }
            Object.keys(next).forEach(dayKey => {
              const dayMix = next[Number(dayKey)]
              if (!dayMix) return
              if (dayMix[appointmentType.unique_key] !== undefined) {
                const { [appointmentType.unique_key]: _removed, ...rest } = dayMix
                next[Number(dayKey)] = rest
              }
            })
            return next
          })
        }
      },
    )

  return (
    <Box sx={{ p: '16px', boxShadow: 'none' }}>
      {loadingSchedules ? <Loader /> : null}
      {!providerData ? (
        <Box sx={{ display: 'flex-start' }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: '500' }}>
              Provider Shifts
            </Typography>
          </Box>
          <Box sx={{ my: 2 }}>
            <Autocomplete<string>
              size="small"
              disabled={loadingSchedules || loadingScheduleForProvider}
              options={Object.keys(providerFilter).sort()}
              value={selectedProvider}
              renderInput={params => <TextField {...params} label="Provider" />}
              onChange={(_, selected) => {
                onProviderChange(selected || '')
              }}
              style={{ width: 280 }}
            />
          </Box>
          <Box sx={{ mt: 3 }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={
                loadingSchedules || !selectedProvider || loadingProvider === selectedProvider
              }
              onClick={() => void handleEditProvider(selectedProvider)}
            >
              {loadingProvider === selectedProvider ? 'Loading...' : 'Edit Provider'}
            </Button>
          </Box>
          {hasPendingDrafts && (
            <Box sx={{ mt: 4 }}>
              {pendingDrafts.map((draft, index) => (
                <Box
                  key={index}
                  sx={{ display: 'flex', alignItems: 'center', bgcolor: yellow[50], p: 2, mb: 1 }}
                >
                  <Typography variant="body1" sx={{ fontWeight: '500', mr: 1 }}>
                    {draft.provider_name}: Ingestion job in progress
                  </Typography>
                  <Button
                    color="primary"
                    onClick={loadPendingDrafts}
                    disabled={loadingPendingDrafts}
                  >
                    {loadingPendingDrafts ? 'Refreshing...' : 'Refresh'}
                  </Button>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      ) : (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="h5" sx={{ fontWeight: '500' }}>
                {`${providerData.first_name} ${providerData.last_name}`}
              </Typography>
              {isIngestionInProgress ? (
                <Box sx={{ display: 'flex', alignItems: 'center', ml: 2, bgcolor: yellow[50] }}>
                  <Typography variant="body1" sx={{ fontWeight: '500', mr: 1 }}>
                    Ingestion job in progress
                  </Typography>
                  <Button color="primary" onClick={() => void handleEditProvider(selectedProvider)}>
                    Refresh
                  </Button>
                </Box>
              ) : null}
              {hasReviewPendingJob ? (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    ml: 2,
                    p: 1,
                    bgcolor: yellow[100],
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="body1" sx={{ color: 'text.primary', fontWeight: '500' }}>
                    You have a draft job pending for review
                  </Typography>
                </Box>
              ) : null}
            </Box>
            <Box>
              <Button
                color="primary"
                disabled={
                  loadingSchedules ||
                  isUpdatingIngestionJob ||
                  isIngestionInProgress ||
                  isUpdatingAppointmentTypeMapping
                }
                onClick={() => updateIngestionJob(REJECTED_STATUS)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={
                  loadingSchedules ||
                  !(isVisitMixEdited || ingestionJobId) ||
                  isUpdatingIngestionJob ||
                  isIngestionInProgress ||
                  isUpdatingAppointmentTypeMapping ||
                  (impactData ? impactData.validation_errors.length > 0 : false)
                }
                onClick={() => updateIngestionJob(APPROVED_STATUS)}
              >
                Save
              </Button>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: '500' }}>
              {`${
                providerData.is_taking_new_patients
                  ? 'Is accepting new patients'
                  : 'Is not accepting new patients'
              }`}
            </Typography>
          </Box>

          {/* Pending Changes UI*/}
          {hasPendingChanges && (
            <Box sx={{ mt: 2, p: 2, bgcolor: yellow[100], borderRadius: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: '500' }}>
                    Pending Changes
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {(() => {
                      const pendingDays = Object.keys(pendingShiftChanges).length
                      const hasExceptionChanges =
                        pendingExceptionChanges.edited.length > 0 ||
                        pendingExceptionChanges.deleted.length > 0
                      const parts: string[] = []

                      if (pendingDays > 0) {
                        parts.push(`${pendingDays} day${pendingDays > 1 ? 's' : ''} of shifts`)
                      }
                      if (hasExceptionChanges) {
                        parts.push('exceptions')
                      }

                      return `You have unsaved changes to ${parts.join(
                        ' and ',
                      )}. Submit all changes to see the impact.`
                    })()}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={handleClearPendingChanges}
                    disabled={isCreatingScheduleIngestionJob}
                  >
                    Clear All
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => void handleSubmitAllPendingChanges()}
                    disabled={isCreatingScheduleIngestionJob}
                  >
                    {isCreatingScheduleIngestionJob ? 'Submitting...' : 'Submit All Changes'}
                  </Button>
                </Box>
              </Box>
            </Box>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1.5 }}>
            <Typography sx={{ fontSize: '2rem' }}>Visit Template</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'baseline', mt: 3 }}>
            <Box>
              {groupedAppointmentTypes.length > 0 && currentSchedule ? (
                <AppointmentTypeMappingSummary
                  groupedAppointmentTypes={groupedAppointmentTypes}
                  mappedAppointmentTypeKeys={mappedAppointmentTypeKeys}
                  onToggleMapping={toggleAppointmentTypeMapping}
                  disabled={
                    loadingSchedules ||
                    !providerData?.allow_edit_schedule ||
                    isIngestionInProgress ||
                    hasReviewPendingJob ||
                    isUpdatingAppointmentTypeMapping ||
                    isVisitMixEdited
                  }
                  disabledReason={
                    isVisitMixEdited
                      ? 'Save or discard visit mix changes before editing appointment types'
                      : undefined
                  }
                />
              ) : null}
              <Box sx={{ mt: 2, display: 'flex' }}>
                <Tabs
                  textColor="primary"
                  indicatorColor="primary"
                  variant="scrollable"
                  scrollButtons="auto"
                  value={selectedTab}
                  onChange={handleTabChange}
                >
                  {TAB_KEYS.map((v, i) => {
                    // Check if this day has pending changes
                    const dayOfWeek = v === EXCEPTIONSTAB ? undefined : TABS[v].dayOfWeek
                    const hasPendingChangesForDay = dayOfWeek && pendingShiftChanges[dayOfWeek]

                    // Check if exceptions tab has pending changes
                    const hasPendingExceptionChanges =
                      v === EXCEPTIONSTAB &&
                      (pendingExceptionChanges.edited.length > 0 ||
                        pendingExceptionChanges.deleted.length > 0)

                    return (
                      <Tab
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {v}
                            {(hasPendingChangesForDay || hasPendingExceptionChanges) && (
                              <Box
                                sx={{
                                  ml: 0.5,
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  bgcolor: 'warning.main',
                                }}
                              />
                            )}
                          </Box>
                        }
                        value={v}
                        key={i}
                        sx={{
                          minWidth: 60,
                          minHeight: 0,
                        }}
                      />
                    )
                  })}
                </Tabs>
              </Box>
              {selectedTab == EXCEPTIONSTAB ? (
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: '1.8rem', fontWeight: '500' }}>
                      Exceptions and Time Off
                    </Typography>
                    <Box sx={{ ml: 1 }}>
                      <IconButton
                        color="secondary"
                        onClick={() => setEditingShiftExceptionsData(shiftExceptionsData)}
                        disabled={
                          loadingSchedules ||
                          !providerData?.allow_edit_schedule ||
                          isIngestionInProgress ||
                          hasReviewPendingJob
                        }
                        sx={theme => ({
                          '&:hover': {
                            backgroundColor: 'transparent',
                            color: theme.palette.secondary[700],
                          },
                          borderRadius: 'unset',
                        })}
                      >
                        <EditIcon color="inherit" sx={{ fontSize: '1.8rem', fontWeight: 500 }} />
                      </IconButton>
                    </Box>
                    <Box>
                      <IconButton
                        color="secondary"
                        onClick={() => void handleHistoryExceptionButtonClick()}
                        disabled={
                          loadingSchedules ||
                          !providerData?.allow_edit_schedule ||
                          isIngestionInProgress ||
                          isLoadingHistoricalShiftExceptions ||
                          !providerData ||
                          hasReviewPendingJob
                        }
                        sx={theme => ({
                          '&:hover': {
                            backgroundColor: 'transparent',
                            color: theme.palette.secondary[700],
                          },
                          borderRadius: 'unset',
                        })}
                      >
                        <HistoryIcon color="inherit" sx={{ fontSize: '2rem', fontWeight: 500 }} />
                      </IconButton>
                    </Box>
                  </Box>

                  <Box sx={{ mt: 1 }}>
                    <TableContainer>
                      <Table id="shift-exceptions-table" sx={TABLE_SX}>
                        <TableBody>
                          {shiftExceptionsData.map((v, i) => (
                            <TableRow key={i}>
                              <TableCell sx={DATE_CELL_SX}>
                                <Typography sx={{ fontSize: '1.6rem', fontWeight: '500' }}>
                                  {formatPeriodDate(v.period)}
                                </Typography>
                              </TableCell>
                              <TableCell sx={CELL_SX}>
                                <Typography sx={{ fontSize: '1.6rem' }}>
                                  {formatPeriodTime(v.period)}
                                </Typography>
                              </TableCell>
                              <TableCell sx={CELL_SX}>
                                <Typography sx={{ fontSize: '1.4rem' }}>
                                  {v.reason || 'OOO'}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: '1.8rem', fontWeight: '500' }}>
                      Visit Mix
                    </Typography>
                    <Box sx={{ ml: 1 }}>
                      <Tooltip
                        title={
                          providerData?.physician == null
                            ? 'This provider has no linked physician, so visit mix cannot be configured.'
                            : ''
                        }
                      >
                        <span>
                          <IconButton
                            color="secondary"
                            onClick={() => setEditingVisitMixData(visitMixData)}
                            disabled={
                              loadingSchedules ||
                              !providerData?.allow_edit_schedule ||
                              isIngestionInProgress ||
                              hasReviewPendingJob ||
                              appointmentTypesData.length === 0 ||
                              providerData?.physician == null
                            }
                            sx={theme => ({
                              '&:hover': {
                                backgroundColor: 'transparent',
                                color: theme.palette.secondary[700],
                              },
                              borderRadius: 'unset',
                            })}
                          >
                            <EditIcon
                              color="inherit"
                              sx={{ fontSize: '1.8rem', fontWeight: 500 }}
                            />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </Box>
                  <Box sx={{ mt: 2 }}>
                    <TableContainer>
                      {groupedAppointmentTypes.length > 0 &&
                      visitMixData[TABS[selectedTab].dayOfWeek] ? (
                        <Table id="visit-mix-table" sx={TABLE_SX}>
                          <TableHead>
                            <TableRow>
                              {groupedAppointmentTypes.map(
                                ({ group }) =>
                                  group && (
                                    <TableCell key={group.id} sx={CELL_SX}>
                                      <Typography sx={{ fontSize: '1.6rem', fontWeight: '500' }}>
                                        {group.title}
                                      </Typography>
                                    </TableCell>
                                  ),
                              )}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            <TableRow>
                              {groupedAppointmentTypes.map(({ group }) => {
                                if (!group) return null
                                const groupKey = `group_${group.id}`
                                return (
                                  <TableCell key={group.id} sx={CELL_SX}>
                                    <Typography sx={{ fontSize: '1.6rem' }}>{`${
                                      visitMixData[TABS[selectedTab].dayOfWeek]?.[groupKey]
                                        ?.percentage ?? '–'
                                    }%`}</Typography>
                                  </TableCell>
                                )
                              })}
                            </TableRow>
                          </TableBody>
                        </Table>
                      ) : null}
                    </TableContainer>
                  </Box>
                  <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: '1.8rem', fontWeight: '500' }}>Shifts</Typography>
                    <Box sx={{ ml: 1 }}>
                      <IconButton
                        color="secondary"
                        onClick={() => setEditingShiftsData(currentShiftsData)}
                        disabled={
                          loadingSchedules ||
                          !providerData?.allow_edit_schedule ||
                          isIngestionInProgress ||
                          hasReviewPendingJob
                        }
                        sx={theme => ({
                          '&:hover': {
                            backgroundColor: 'transparent',
                            color: theme.palette.secondary[700],
                          },
                          borderRadius: 'unset',
                        })}
                      >
                        <EditIcon color="inherit" sx={{ fontSize: '1.8rem', fontWeight: 500 }} />
                      </IconButton>
                    </Box>
                    <Box>
                      <IconButton
                        color="secondary"
                        onClick={() => void handleHistoryButtonClick()}
                        disabled={
                          loadingSchedules ||
                          !providerData?.allow_edit_schedule ||
                          isIngestionInProgress ||
                          isLoadingHistoricalShifts ||
                          !providerData ||
                          hasReviewPendingJob
                        }
                        sx={theme => ({
                          '&:hover': {
                            backgroundColor: 'transparent',
                            color: theme.palette.secondary[700],
                          },
                          borderRadius: 'unset',
                        })}
                      >
                        <HistoryIcon color="inherit" sx={{ fontSize: '2rem', fontWeight: 500 }} />
                      </IconButton>
                    </Box>
                  </Box>
                  <Box sx={{ mt: 2 }}>
                    <TableContainer>
                      <Table id="shifts-table" sx={TABLE_SX}>
                        <TableBody>
                          {currentShiftsData.map((v, i) => (
                            <TableRow key={i}>
                              <TableCell sx={CELL_SX}>
                                <Typography sx={{ fontSize: '1.6rem' }}>
                                  {`${formatTime(v.start_time)} – ${formatTime(v.stop_time)}`}
                                </Typography>
                              </TableCell>
                              <TableCell sx={CELL_SX}>
                                <Typography sx={{ fontSize: '1.4rem' }}>
                                  {formatEffectivePeriod(v.effective_period)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </Box>
              )}
            </Box>
            <Box sx={{ ml: 10 }}>
              {isGettingScheduleIngestionJobImpact ? <Loader /> : null}
              {impactData?.validation_errors.length ? (
                <Box sx={{ mt: 3 }}>
                  <Typography sx={{ fontSize: '1.8rem', fontWeight: '500' }}>
                    Validation Errors
                  </Typography>
                  <Box sx={{ mt: 3, bgcolor: yellow[50] }}>
                    {impactData.validation_errors.map((error, i) => (
                      <Typography key={i} sx={{ fontSize: '1.4rem', fontWeight: '500' }}>
                        {error}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              ) : null}
              <Box sx={{ mt: 4 }}>
                <Typography sx={{ fontSize: '1.8rem', fontWeight: '500' }}>
                  Cancellations
                </Typography>
                {impactData?.impacted_appointments.length ? (
                  <Box sx={{ mt: 3, bgcolor: yellow[50] }}>
                    {impactData.impacted_appointments.map((appt, i) => (
                      <Box key={i} sx={{ display: 'flex' }}>
                        {appt.patient_url ? (
                          <Link
                            href={appt.patient_url}
                            target="blank"
                            sx={{
                              textDecoration: 'none',
                              cursor: 'pointer',
                              transition: '100ms linear color',
                              color: 'inherit',
                              '&:hover': {
                                color: 'primary.main',
                              },
                            }}
                          >
                            <Typography
                              sx={{ display: 'inline', fontSize: '1.4rem', fontWeight: '500' }}
                            >
                              {appt.affected_patient}
                            </Typography>
                          </Link>
                        ) : (
                          <Typography sx={{ fontSize: '1.4rem', fontWeight: '500' }}>
                            {appt.affected_patient}
                          </Typography>
                        )}
                        <Typography sx={{ fontSize: '1.4rem', fontWeight: '500' }}>
                          {`: ${appt.start_time}: ${appt.short_reason}`}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ mt: 3 }}>
                    <Typography sx={{ fontSize: '1.4rem' }}>None</Typography>
                  </Box>
                )}
                <Box sx={{ mt: 4 }}>
                  <Typography sx={{ fontSize: '1.8rem', fontWeight: '500' }}>
                    Unbooked Slot Deletions
                  </Typography>
                  {impactData?.impacted_appointment_slots.length ? (
                    <Box sx={{ mt: 3, bgcolor: yellow[50] }}>
                      {impactData.impacted_appointment_slots.map((appt, i) => (
                        <Typography key={i} sx={{ fontSize: '1.4rem', fontWeight: '500' }}>
                          {`${appt.start_time}: ${appt.short_reason}`}
                        </Typography>
                      ))}
                    </Box>
                  ) : (
                    <Box sx={{ mt: 3 }}>
                      <Typography sx={{ fontSize: '1.4rem' }}>None</Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
          {selectedTab == EXCEPTIONSTAB ? (
            <EditShiftExceptionTimingsModal
              shiftExceptions={editingShiftExceptionsData}
              isHistory={isHistory}
              loading={isCreatingScheduleIngestionJob}
              handleCancel={() => {
                setIsHistory(false)
                setEditingShiftExceptionsData(null)
              }}
              ingestionJobId={ingestionJobId}
              providerId={providerData.pk}
              handleSubmit={handleShiftExceptionSubmit}
            />
          ) : (
            <>
              <EditShiftTimingsModal
                shifts={editingShiftsData}
                dayOfWeek={TABS[selectedTab].dayOfWeek}
                fullDayName={TABS[selectedTab].fullDayName}
                isHistory={isHistory}
                loading={isCreatingScheduleIngestionJob || isLoadingHistoricalShifts}
                handleCancel={() => {
                  setIsHistory(false)
                  setEditingShiftsData(null)
                }}
                handleSubmit={handleShiftSubmit}
              />
              <EditVisitMixModal
                visitMixGroups={groupedAppointmentTypes}
                visitMix={editingVisitMixData}
                dayOfWeek={TABS[selectedTab].dayOfWeek}
                fullDayName={TABS[selectedTab].fullDayName}
                handleCancel={() => setEditingVisitMixData(null)}
                handleSubmit={handleVisitMixSubmit}
                mappedAppointmentTypeKeys={mappedAppointmentTypeKeys}
              />
            </>
          )}
        </Box>
      )}
    </Box>
  )
}

export default ProviderShiftSettings
